import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { FaPills, FaFileMedical, FaIndustry, FaMoneyBillWave } from "react-icons/fa";
import { FiArrowLeft, FiSave, FiCheckCircle } from "react-icons/fi";
import Swal from "sweetalert2";

const EditObat = () => {
  const param = useParams();
  const flag = param["*"];
  const navigate = useNavigate();
  const [obatDetail, setObatDetail] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const fetchDetailObat = async (paramStr) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    let newParam = encodeURI(paramStr);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/drug/detail/${newParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (resp.status === 200) {
        setObatDetail(resp.data.data || {});
      }
    } catch (err) {
      console.error("Gagal mengambil data obat:", err);
      Swal.fire({
        icon: "error",
        title: "Gagal Memuat Detail",
        text: "Terjadi kesalahan saat memuat data obat.",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetailObat(flag);
  }, [flag]);

  const handleChange = (e) => {
    setObatDetail((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const payload = {
      name: obatDetail.name,
      dosis: obatDetail.dosis,
      isi: obatDetail.isi,
      harga_jual: parseFloat(obatDetail.harga_jual) || 0,
      harga_beli: parseFloat(obatDetail.harga_beli) || 0,
      produsen: obatDetail.produsen,
      description: obatDetail.desc || obatDetail.description || "",
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/v1/drug/${obatDetail.id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await Swal.fire({
        title: "Berhasil Diperbarui!",
        text: `Data obat ${obatDetail.name} telah disimpan.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      navigate("/obat-paket/obat");
    } catch (err) {
      console.error("Edit error:", err);
      Swal.fire({
        title: "Gagal Memperbarui",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/obat-paket/obat")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Edit Data Obat: {obatDetail.name}
            </h1>
            <p className="text-xs text-gray-500">
              Perbarui harga katalog, dosis, dan produsen obat
            </p>
          </div>
        </div>
      </motion.div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-8 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <form onSubmit={handleSubmit} className="space-y-6 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Nama Obat */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nama Obat *
              </label>
              <div className="relative">
                <FaFileMedical className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  name="name"
                  value={obatDetail.name || ""}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            {/* Dosis */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Dosis & Satuan *
              </label>
              <input
                type="text"
                name="dosis"
                value={obatDetail.dosis || ""}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                required
              />
            </div>

            {/* Produsen */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Produsen / Pabrik PBF *
              </label>
              <div className="relative">
                <FaIndustry className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  name="produsen"
                  value={obatDetail.produsen || ""}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            {/* Harga Beli */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Harga Beli (Rp) *
              </label>
              <div className="relative">
                <FaMoneyBillWave className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  name="harga_beli"
                  value={obatDetail.harga_beli || ""}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            {/* Harga Jual */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Harga Jual (Rp) *
              </label>
              <div className="relative">
                <FaMoneyBillWave className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-600 w-4 h-4" />
                <input
                  type="number"
                  name="harga_jual"
                  value={obatDetail.harga_jual || ""}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-emerald-800 font-mono font-extrabold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            {/* Isi Kemasan */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Kemasan / Isi
              </label>
              <input
                type="text"
                name="isi"
                value={obatDetail.isi || ""}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            {/* Deskripsi */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Deskripsi Tambahan / Indikasi
              </label>
              <textarea
                rows={3}
                name="desc"
                value={obatDetail.desc || obatDetail.description || ""}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/obat-paket/obat")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loadingSubmit}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{loadingSubmit ? "Menyimpan..." : "Simpan Perubahan"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditObat;
